import { useId, useRef, useState, type DragEvent } from "react";
import { previewImport, useApplyImport, type ImportPreview, type StepDoc } from "@/app/queries";
import { plural } from "@/lib/plural";
import { CardHead } from "./shared";

/** «Импорт списком»: файл → предпросмотр с сервера → «добавить N ресурсов». */
export function ImportCard({ stepId, flush, adopt }: { stepId: string; flush: () => Promise<void>; adopt: (doc: StepDoc) => void }) {
  const inputId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<File | null>(null);
  const apply = useApplyImport(stepId);

  const load = async (file: File) => {
    setLastFile(file);
    setParsing(true);
    setError(null);
    try {
      setPreview(await previewImport(stepId, file));
    } catch (e) {
      setPreview(null);
      setError(e instanceof Error ? e.message : "Не удалось разобрать файл.");
    } finally {
      setParsing(false);
    }
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) void load(f);
  };
  const reset = () => {
    setPreview(null);
    setError(null);
    if (fileRef.current) fileRef.current.value = "";
  };
  const applyRows = async () => {
    if (!preview) return;
    await flush();
    try {
      const doc = await apply.mutateAsync(preview.rows);
      adopt(doc);
      reset();
    } catch {
      /* ошибка показана ниже из apply.error */
    }
  };

  const n = preview?.rows.length ?? 0;
  const state = parsing ? "разбираем…" : preview ? `разобран: ${n} ${plural(n, "строка", "строки", "строк")}` : "не загружен";
  const k = preview?.without_guide ?? 0;

  return (
    <section className="overflow-hidden rounded-md border border-bd bg-card" data-testid="import-card">
      <CardHead title="импорт списком" meta="csv · xlsx · до 5 МБ" right={<span className="font-mono text-[11px] text-acc">{state}</span>} />
      <div className="grid grid-cols-[minmax(0,1fr)_236px] items-start gap-4 p-3.5">
        <div>
          <input
            ref={fileRef}
            id={inputId}
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="sr-only"
            aria-label="файл импорта"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void load(f);
            }}
          />
          <label
            htmlFor={inputId}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="block w-full cursor-pointer rounded-md border border-dashed border-bd2 p-5 text-center font-mono text-[12.5px] leading-[1.6] text-ink2 hover:border-acc"
          >
            перетащи файл сюда или выбери с диска
            <br />
            <span className="text-[11px] text-mut">колонки: название · тип · ссылка · длительность · юниты гайда</span>
          </label>
          {error && (
            <p className="mt-2.5 flex items-center gap-2.5 font-mono text-[11.5px] text-err">
              <span>{error}</span>
              {lastFile && (
                <button type="button" onClick={() => void load(lastFile)} className="cursor-pointer rounded-sm border border-err bg-transparent px-2 py-1 font-mono text-[11px] text-err">
                  повторить
                </button>
              )}
            </p>
          )}
          {preview && (
            <div className="mt-3 overflow-hidden rounded-[5px] border border-bd" data-testid="import-preview">
              <div className="grid grid-cols-[minmax(0,1.4fr)_84px_minmax(0,1fr)_92px] gap-2.5 border-b border-bd bg-bg2 px-3 py-2 font-mono text-[10px] uppercase tracking-[.05em] text-mut">
                <span>название</span>
                <span>тип</span>
                <span>ссылка</span>
                <span>юниты</span>
              </div>
              {preview.rows.map((r, i) => {
                const u = r.units.length;
                return (
                  <div key={i} className="grid grid-cols-[minmax(0,1.4fr)_84px_minmax(0,1fr)_92px] items-center gap-2.5 border-b border-bd px-3 py-[9px]" data-testid="import-row">
                    <span className="truncate text-[12.5px]">{r.title}</span>
                    <span className="font-mono text-[11px] text-ink2">{r.kind}</span>
                    <span className="truncate font-mono text-[11px] text-mut">{r.url || "—"}</span>
                    <span className="font-mono text-[11px]" style={{ color: u ? "var(--ok)" : "var(--warn)" }}>
                      {u ? `${u} ${plural(u, "юнит", "юнита", "юнитов")}` : "нет"}
                    </span>
                  </div>
                );
              })}
              <div className="flex flex-wrap items-center gap-[9px] px-3 py-[11px]">
                <button type="button" onClick={() => void applyRows()} disabled={apply.isPending} className="cursor-pointer rounded-sm border border-acc bg-acc px-3.5 py-2 font-mono text-[12px] text-accink">
                  {apply.isPending ? "добавляем…" : `добавить ${n} ${plural(n, "ресурс", "ресурса", "ресурсов")}`}
                </button>
                <button type="button" onClick={reset} className="cursor-pointer rounded-sm border border-bd2 bg-transparent px-3.5 py-2 font-mono text-[12px] text-ink2">
                  отмена
                </button>
                {k > 0 && (
                  <span className="font-mono text-[11px] text-warn">
                    {k} {plural(k, "строка", "строки", "строк")} без гайда — {k === 1 ? "добавится" : "добавятся"} как «без гайда»
                  </span>
                )}
                {apply.isError && <span className="font-mono text-[11px] text-err">{apply.error.message}</span>}
              </div>
            </div>
          )}
        </div>
        <div className="border-l border-bd pl-4">
          <div className="mb-[9px] font-mono text-[11px] text-mut">формат файла</div>
          <div className="font-mono text-[11px] leading-[1.7] text-ink2">
            title · kind
            <br />
            url · duration
            <br />
            guide_units
          </div>
          <a href="/api/admin/import-template.csv" download className="mt-[11px] inline-block font-mono text-[11.5px] text-acc no-underline hover:underline">
            ↓ скачать шаблон .csv
          </a>
          <p className="m-0 mt-[11px] text-[11.5px] leading-[1.5] text-mut text-pretty">Юниты гайда можно разметить и позже — вручную в карточке ресурса.</p>
        </div>
      </div>
    </section>
  );
}
